/**
 * Predefined Unit Definitions
 * Shared by frontend and backend for consistent unit validation and conversion.
 *
 * Units are grouped into weight, volume, and count.
 * Same-group conversions use `toBase` multipliers (base units: g, ml).
 * Cross-group conversions require manual user input.
 */

import type { IngredientCategory } from './api';

// =============================================================================
// Quantity Precision
// =============================================================================
// Matches the database schema: @db.Decimal(10, QUANTITY_DECIMAL_PLACES)
// Change these if the schema precision changes — all formatting, clamping,
// and input constraints derive from them.

/** Number of decimal places stored in the database for quantity fields */
export const QUANTITY_DECIMAL_PLACES = 4;

/** Round to the quantity precision the database stores (Decimal(10,4)). */
export function roundQuantity(value: number): number {
  return Number(value.toFixed(QUANTITY_DECIMAL_PLACES));
}

/** Smallest positive value representable at QUANTITY_DECIMAL_PLACES precision */
export const QUANTITY_MIN_POSITIVE = Math.pow(10, -QUANTITY_DECIMAL_PLACES); // 0.0001

/** Largest value the Decimal(10,4) quantity columns can store */
export const QUANTITY_MAX = 999_999.9999;
export const QUANTITY_MAX_MESSAGE = 'Quantity must be 999,999.9999 or less';

/** Per-serving recipe quantity ceiling (guards against unit mistakes) */
export const RECIPE_QUANTITY_PER_SERVING_MAX = 10_000;
export const RECIPE_QUANTITY_PER_SERVING_MAX_MESSAGE = 'Per-serving quantity must be 10,000 or less. Check the unit.';

// =============================================================================
// Thaali Sizes
// =============================================================================

export const RSVP_SIZES = ['S', 'M', 'L'] as const;
export type RSVPSizeCode = typeof RSVP_SIZES[number];
export type RSVPSizeCounts = Record<RSVPSizeCode, number>;
export type RSVPScaleRatios = Record<RSVPSizeCode, number>;

export const RSVP_SIZE_LABELS: Record<RSVPSizeCode, string> = {
  S: 'Small',
  M: 'Medium',
  L: 'Large',
};

/** Default procurement scale relative to a Large thaali. */
export const DEFAULT_RSVP_SIZE_SCALE_RATIOS: RSVPScaleRatios = {
  S: 0.5,
  M: 0.75,
  L: 1,
};

/** Backward-compatible alias for legacy M/L procurement code and snapshots. */
export const MEDIUM_TO_LARGE_RATIO = DEFAULT_RSVP_SIZE_SCALE_RATIOS.M;

export function createEmptyRsvpSizeCounts(): RSVPSizeCounts {
  return { S: 0, M: 0, L: 0 };
}

export function createRsvpSizeCounts(overrides: Partial<RSVPSizeCounts> = {}): RSVPSizeCounts {
  return {
    S: overrides.S ?? 0,
    M: overrides.M ?? 0,
    L: overrides.L ?? 0,
  };
}

/**
 * On-hand stock left for a weekly staple after this week's recipe need for the
 * same ingredient has consumed it. Inventory is tracked once per ingredient, so a
 * RECIPE row and a STAPLE row for the same ingredient must not both subtract the
 * full on-hand amount. Recipe rows consume stock first; the staple tops up the rest.
 */
export function netStapleOnHand(onHand: number, recipeNeed: number): number {
  const safeOnHand = Number.isFinite(onHand) ? Math.max(0, onHand) : 0;
  const safeNeed = Number.isFinite(recipeNeed) ? Math.max(0, recipeNeed) : 0;
  return Math.max(0, safeOnHand - safeNeed);
}

/**
 * Round a purchase need up to the next multiple of an ingredient's order
 * increment (the smallest amount that can be bought: a bag, a case, a pack).
 * Issue #140: this replaces the whole-unit ceiling. An increment of 1 is the
 * old behaviour. Shared by the server (list generation) and the client
 * (inventory edits, RSVP simulation) so the rule lives in one place.
 *
 * - value <= 0 -> 0 (nothing to buy)
 * - invalid increment (<= 0, NaN) -> whole units
* - computed in integer quanta of QUANTITY_DECIMAL_PLACES, so it is exact for
 *   every storable value and floating-point noise at a multiple (0.1 * 3) does
 *   not bump to the next pack
 */
export function roundUpToIncrement(value: number, increment: number): number {
  const step = Number.isFinite(increment) && increment > 0 ? increment : 1;
  // Work in integer quanta of 10^-QUANTITY_DECIMAL_PLACES so the arithmetic is exact
  // for every storable value (0.1 * 3 = 0.30000000000000004 would otherwise bump a pack).
  const scale = Math.pow(10, QUANTITY_DECIMAL_PLACES);
  const needQuanta = Number.isFinite(value) ? Math.round(value * scale) : 0;
  if (needQuanta <= 0) return 0;
  const stepQuanta = Math.max(1, Math.round(step * scale));
  return (Math.ceil(needQuanta / stepQuanta) * stepQuanta) / scale;
}

/**
 * An ingredient's order increment expressed in a given row unit. The increment
 * is stored in the ingredient's default unit; a procurement row may carry a
 * different (legacy) unit. Invalid increments and non-convertible units
 * (count vs grams) fall back to 1 (whole units). Shared by server and client.
 */
export function convertOrderIncrementToUnit(
  orderIncrement: number | string | { toString(): string } | null | undefined,
  defaultUnit: string,
  unit: string,
): number {
  const increment = Number(orderIncrement);
  if (!Number.isFinite(increment) || increment <= 0) return 1;
  const multiplier = getAutoConversionMultiplier(defaultUnit, unit);
  if (multiplier === null) return 1;
  return Number((increment * multiplier).toFixed(QUANTITY_DECIMAL_PLACES));
}

// =============================================================================
// Default unit by ingredient category
// =============================================================================

/**
 * The unit a new ingredient starts with, per category, matching how the kitchen
 * buys: produce, meat, seafood, dairy, grains and frozen goods by the pound,
 * spices by the gram, oils by the gallon, canned goods by the can, beverages by
 * the liter. OTHER has no suggestion. Shared by the Kitchen Library form and the
 * recipe quick-create so both offer the same starting point.
 */
export const DEFAULT_UNIT_BY_CATEGORY: Partial<Record<IngredientCategory, string>> = {
  PRODUCE: 'lbs',
  MEAT: 'lbs',
  SEAFOOD: 'lbs',
  DAIRY: 'lbs',
  GRAINS: 'lbs',
  FROZEN: 'lbs',
  SPICES: 'g',
  OILS: 'gallons',
  CANNED: 'cans',
  BEVERAGES: 'liters',
};

export function getDefaultUnitForCategory(category: IngredientCategory): string | null {
  return DEFAULT_UNIT_BY_CATEGORY[category] ?? null;
}

/**
 * Unit to show after the category of a NEW ingredient changes. A unit the user
 * picked themselves (unitTouched) is always kept. Otherwise a unit that is empty
 * or still the previous category's suggestion follows the new category, and any
 * other value is kept.
 */
export function resolveUnitForCategoryChange(args: {
  previousCategory: IngredientCategory;
  nextCategory: IngredientCategory;
  currentUnit: string;
  /** True when the user picked the current unit themselves; such a unit is always kept. */
  unitTouched?: boolean;
}): string {
  const { previousCategory, nextCategory, currentUnit, unitTouched = false } = args;
  if (unitTouched && currentUnit !== '') return currentUnit;
  const wasSuggestion = currentUnit === '' || currentUnit === getDefaultUnitForCategory(previousCategory);
  if (!wasSuggestion) return currentUnit;
  return getDefaultUnitForCategory(nextCategory) ?? '';
}

// =============================================================================
// Unit suggestion from usage history and from the ingredient name
// =============================================================================

/** How many past uses of an ingredient the unit suggestion looks at. */
export const UNIT_HISTORY_WINDOW = 10;

export interface UnitUse {
  unit: string;
  /** ISO timestamp of the use (recipe row saved, procurement row created). */
  at: string;
}

export interface UnitHistorySuggestion {
  unit: string;
  /** Uses of the suggested unit within the window. */
  used: number;
  /** Uses considered (valid units only, at most UNIT_HISTORY_WINDOW). */
  of: number;
}

/**
 * The unit an ingredient is normally used in: the majority unit among its last
 * UNIT_HISTORY_WINDOW uses; a tie goes to the unit used most recently. One use is
 * enough. Units the app does not know (legacy "ct", "lb") are ignored. Returns
 * null when there is no usable history.
 */
export function suggestUnitFromHistory(uses: UnitUse[]): UnitHistorySuggestion | null {
  const recent = uses
    .filter((u) => (VALID_UNITS as readonly string[]).includes(u.unit))
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, UNIT_HISTORY_WINDOW);
  if (recent.length === 0) return null;

  const counts = new Map<string, number>();
  const latest = new Map<string, string>();
  for (const u of recent) {
    counts.set(u.unit, (counts.get(u.unit) ?? 0) + 1);
    if (!latest.has(u.unit)) latest.set(u.unit, u.at);
  }
  const ranked = [...counts.entries()].sort((a, b) =>
    b[1] - a[1] || (latest.get(b[0]) ?? '').localeCompare(latest.get(a[0]) ?? ''));
  const [unit, used] = ranked[0]!;
  return { unit, used, of: recent.length };
}

/**
 * Unit suggested from the name alone, for fresh herbs that are bought by the
 * bunch (or pack) whatever their category says. Ground, dried and powdered
 * forms are not matched. Returns null when the name says nothing.
 */
export function suggestUnitByName(name: string): string | null {
  const n = name.trim().toLowerCase();
  if (!n) return null;
  if (/\b(powder|ground|dried|dry|seeds?|kasoori|kasuri|paste|sauce|juice|oil|frozen)\b/.test(n)) return null;
  if (/\b(curry leaves|kari patta|kadi patta)\b/.test(n)) return 'packs';
  if (/\b(cilantro|coriander leaves|mint|methi leaves|fenugreek leaves|green onions?|spring onions?|scallions?|celery|dill|parsley|basil|thyme|rosemary|lemongrass)\b/.test(n)) return 'bunches';
  return null;
}

export type UnitSource = 'category' | 'suggestion' | 'user';

/**
 * What a form should do with the unit after a name lookup resolves. A unit the
 * user picked is never changed. A suggested unit replaces an untouched unit. When
 * the lookup finds nothing and the current unit came from an earlier suggestion
 * (for example "Basil" became "Basil Seeds"), the category rule takes over again.
 * Returns null when the unit should stay as it is.
 */
export function applyUnitSuggestion(args: {
  suggestedUnit: string | null;
  source: UnitSource;
  category: IngredientCategory;
}): { unit: string; source: UnitSource } | null {
  const { suggestedUnit, source, category } = args;
  if (source === 'user') return null;
  if (suggestedUnit) return { unit: suggestedUnit, source: 'suggestion' };
  if (source === 'suggestion') return { unit: getDefaultUnitForCategory(category) ?? '', source: 'category' };
  return null;
}

export function isRSVPSizeCode(value: unknown): value is RSVPSizeCode {
  return typeof value === 'string' && (RSVP_SIZES as readonly string[]).includes(value);
}

/** Format a numeric quantity to a display string, trimming trailing zeros */
export function formatScaledQuantity(value: number): string {
  return value.toFixed(QUANTITY_DECIMAL_PLACES).replace(/\.?0+$/, '');
}

// =============================================================================
// Unit Definitions
// =============================================================================

export type UnitGroup = 'weight' | 'volume' | 'count';

export interface UnitDefinition {
  value: string;
  label: string;
  group: UnitGroup;
  /** Multiplier to convert to group base unit (g for weight, ml for volume). null = no auto-conversion */
  toBase: number | null;
}

export const UNIT_DEFINITIONS: UnitDefinition[] = [
  // Weight (base: grams)
  { value: 'g', label: 'Grams (g)', group: 'weight', toBase: 1 },
  { value: 'kg', label: 'Kilograms (kg)', group: 'weight', toBase: 1000 },
  { value: 'oz', label: 'Ounces (oz)', group: 'weight', toBase: 28.3495 },
  { value: 'lbs', label: 'Pounds (lbs)', group: 'weight', toBase: 453.592 },

  // Volume (base: milliliters)
  { value: 'ml', label: 'Milliliters (ml)', group: 'volume', toBase: 1 },
  { value: 'liters', label: 'Liters', group: 'volume', toBase: 1000 },
  { value: 'gallons', label: 'Gallons', group: 'volume', toBase: 3785.41 },

  // Count (no base conversion between count types)
  { value: 'count', label: 'Count', group: 'count', toBase: null },
  { value: 'bunches', label: 'Bunches', group: 'count', toBase: null },
  { value: 'cans', label: 'Cans', group: 'count', toBase: null },
  { value: 'packs', label: 'Packs', group: 'count', toBase: null },
  { value: 'dozens', label: 'Dozens', group: 'count', toBase: null },
];

/** Just the string values for Zod enum validation */
export const VALID_UNITS = UNIT_DEFINITIONS.map((u) => u.value) as [string, ...string[]];

/** Lookup by unit value string */
export const UNIT_MAP = new Map<string, UnitDefinition>(
  UNIT_DEFINITIONS.map((u) => [u.value, u])
);

/** Grouped definitions for rendering Select dropdowns */
export const UNIT_GROUPS: Record<UnitGroup, UnitDefinition[]> = {
  weight: UNIT_DEFINITIONS.filter((u) => u.group === 'weight'),
  volume: UNIT_DEFINITIONS.filter((u) => u.group === 'volume'),
  count: UNIT_DEFINITIONS.filter((u) => u.group === 'count'),
};

/** Group display labels for Select group headers */
export const UNIT_GROUP_LABELS: Record<UnitGroup, string> = {
  weight: 'Weight',
  volume: 'Volume',
  count: 'Count',
};

/** Spellings (including legacy library units lb/ct) → one of the app unit values */
export const UNIT_ALIASES: Record<string, string> = {
  lb: 'lbs', lbs: 'lbs', pound: 'lbs', pounds: 'lbs',
  g: 'g', gm: 'g', gms: 'g', gram: 'g', grams: 'g',
  kg: 'kg', kgs: 'kg', kilo: 'kg', kilos: 'kg', kilogram: 'kg', kilograms: 'kg',
  oz: 'oz', ounce: 'oz', ounces: 'oz',
  ml: 'ml',
  l: 'liters', ltr: 'liters', ltrs: 'liters', litre: 'liters', litres: 'liters', liter: 'liters', liters: 'liters',
  gal: 'gallons', gallon: 'gallons', gallons: 'gallons',
  count: 'count', ct: 'count', pc: 'count', pcs: 'count', nos: 'count', piece: 'count', pieces: 'count',
  bunch: 'bunches', bunches: 'bunches',
  can: 'cans', cans: 'cans',
  pkt: 'packs', pkts: 'packs', pakt: 'packs', pakts: 'packs', pack: 'packs', packs: 'packs', packet: 'packs', packets: 'packs', bag: 'packs', bags: 'packs',
  dozen: 'dozens', dozens: 'dozens',
};

/** One of the app unit values for a typed or stored unit, or null when unknown. */
export function canonicalUnit(raw: string): string | null {
  const unit = UNIT_ALIASES[raw.trim().toLowerCase()];
  return unit && UNIT_MAP.has(unit) ? unit : null;
}

/**
 * Returns a multiplier for converting between units in the same group.
 * Both units are canonicalised first, so legacy lb/ct convert like lbs/count.
 * Returns null when:
 * - Either unit is unknown
 * - Units are in different groups (cross-group: requires manual multiplier)
 * - Either unit has no toBase value (count group)
 */
export function getAutoConversionMultiplier(
  fromUnit: string,
  toUnit: string
): number | null {
  if (fromUnit === toUnit) return 1;
  const fromCanonical = canonicalUnit(fromUnit) ?? fromUnit;
  const toCanonical = canonicalUnit(toUnit) ?? toUnit;
  if (fromCanonical === toCanonical) return 1;

  const from = UNIT_MAP.get(fromCanonical);
  const to = UNIT_MAP.get(toCanonical);

  if (!from || !to) return null;
  if (from.group !== to.group) return null;
  if (from.toBase === null || to.toBase === null) return null;

  return from.toBase / to.toBase;
}

/**
 * Units that add together share a key: the group for units with a base
 * conversion (weight, volume), the unit itself otherwise (count units and
 * unknown legacy units). Keys are equal exactly when getAutoConversionMultiplier
 * is non-null, so a procurement line is one (ingredient, key).
 */
export function getUnitConversionKey(unit: string): string {
  const canonical = canonicalUnit(unit) ?? unit;
  const definition = UNIT_MAP.get(canonical);
  return definition && definition.toBase !== null ? `group:${definition.group}` : `unit:${canonical}`;
}

/**
 * Unit an ingredient's shared on-hand is recorded in for a list: its default
 * unit when at least one of its lines (or no line at all) can net that, else the
 * first line's unit so the only line that can use the on-hand nets it as typed.
 */
export function resolveSharedInventoryUnit(defaultUnit: string, lineUnits: string[]): string {
  if (lineUnits.length === 0 || lineUnits.some((unit) => getAutoConversionMultiplier(defaultUnit, unit) !== null)) {
    return defaultUnit;
  }
  return lineUnits[0]!;
}

/**
 * Unit an on-hand edit is saved in, and the unit the On Hand box is labelled
 * with: the unit already saved, while some line can still convert from it, else
 * resolveSharedInventoryUnit. Shared by the inventory endpoint and the page so
 * the label and the save never drift apart.
 */
export function resolveInventoryUnitOnSave(savedUnit: string | null | undefined, defaultUnit: string, lineUnits: string[]): string {
  if (savedUnit && lineUnits.some((unit) => getAutoConversionMultiplier(savedUnit, unit) !== null)) return savedUnit;
  return resolveSharedInventoryUnit(defaultUnit, lineUnits);
}
